import { inject, Pipe, PipeTransform } from '@angular/core';
import { renderRichText, StoryblokRichTextNode } from '@storyblok/js';
import { storyblokLowQualityMap } from '../consts';
import { PICFLOW_CONFIG } from '../picflow-config';
import { PICFLOW_STORYBLOK } from '../picflow-storyblok';
import { getPageWidth } from './components/block-wrapper/block-wrapper.component';
import { fixHtml } from './custom-render-text';
import { getImageHeight, getImageWidth, getPictureSourceSizes } from './custom-rich-text';
import { getHeadingAnchor } from './get-header-anchor';
import { getStoryblokResizedUrl } from './get-resize-url';
import { BlockStyle } from './interfaces/block-style';

@Pipe({
  name: 'storyblokRichText',
  standalone: true,
})
export class StoryblokRichTextPipe implements PipeTransform {
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private config = inject(PICFLOW_CONFIG);

  transform(
    value: StoryblokRichTextNode<string> | string | undefined | null,
    style?: BlockStyle,
    stripPastedMarks = false
  ): string {
    if (!value) return '';
    if (typeof value === 'string') return value;

    return fixHtml(
      renderRichText(value, {
        resolvers: {
          // Pasting from Google Docs etc. carries underline and text-color
          // marks. Blog articles opt in to render the plain text instead;
          // other templates may use these marks intentionally.
          ...(stripPastedMarks && {
            underline: node => node.text ?? '',
            textStyle: node => node.text ?? '',
          }),
          heading: node => {
            if (!node.content) return '';
            const id = getHeadingAnchor(node.attrs.level, node.content.map(item => item?.['text']).join('') || '');
            return `<h${node.attrs.level} ${id ? `id="${id}"` : ''} class="storyblok-heading">${node.content.map(item => item.text).join('')}</h${node.attrs.level}>`;
          },
          highlight: node => {
            return `<mark>${node.text}</mark>`;
          },
          link: node => {
            const href =
              node.attrs.linktype === 'email'
                ? `mailto:${node.attrs.href.replace(/^mailto:/, '')}`
                : this.storyblokService.getUrlLink({
                    linktype: node.attrs.linktype === 'story' ? 'story' : 'url',
                    url: node.attrs.href,
                    cached_url: node.attrs.href,
                    id: node.attrs.uuid,
                  });
            const anchor = node.attrs.anchor ? `#${node.attrs.anchor}` : '';
            const attrs = {
              href: `${href}${anchor}`,
              target: node.attrs.target,
              rel: node.attrs.rel,
            };

            return `<a ${Object.entries(attrs)
              .map(([key, value]) => (value ? `${key}="${value}"` : ''))
              .join(' ')}>${node.text}</a>`;
          },
          image: node => {
            const url = node.attrs.src.replace(
              '//a.storyblok.com/f/234504',
              this.config.storyblokUrl.replace('https://', '//')
            );

            const maxWidth = getPageWidth(style?.pageWidth, style?.customPageWidth);
            const srcset = getPictureSourceSizes(node.attrs.src, maxWidth).map(
              size =>
                `${getStoryblokResizedUrl(url, size, storyblokLowQualityMap[size], this.config.storyblokUrl)} ${size}w`
            );

            const attrs = {
              src: url,
              srcset: srcset.join(', '),
              alt: node.attrs.alt,
              title: node.attrs.title || '',
              loading: 'lazy',
              width: getImageWidth(url),
              height: getImageHeight(url),
              // Guard against unset page width — never emit "undefinedpx"
              sizes: maxWidth > 0 ? `(max-width: ${maxWidth}px) 100vw, ${maxWidth}px` : '100vw',
            };

            const imageCaption = attrs.title ? `<div class="storyblok-inline-image-caption">${attrs.title}</div>` : '';

            return `<img ${Object.entries(attrs)
              .map(([key, value]) => (value ? `${key}="${value}"` : ''))
              .join(' ')} />${imageCaption}`;
          },
        },
      })
    );
  }
}
